import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Table root
// ---------------------------------------------------------------------------
function Table({ className, ...props }: React.HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <table className={cn('w-full text-sm', className)} {...props} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Head / Header cell
// ---------------------------------------------------------------------------
function TableHead({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('bg-gray-50 dark:bg-gray-800/50', className)} {...props} />
}

interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean
  sortDir?: 'asc' | 'desc' | null
}

function Th({ className, children, sortable, sortDir, onClick, ...props }: ThProps) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400',
        sortable && 'cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-300',
        className,
      )}
      onClick={onClick}
      aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : undefined}
      {...props}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortable && (
          <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">
            {sortDir === 'asc' ? '\u2191' : sortDir === 'desc' ? '\u2193' : '\u2195'}
          </span>
        )}
      </span>
    </th>
  )
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------
function TableBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={cn('divide-y divide-gray-100 dark:divide-gray-700', className)} {...props} />
  )
}

// ---------------------------------------------------------------------------
// Row / Cell
// ---------------------------------------------------------------------------
function Tr({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('transition hover:bg-gray-50/60 dark:hover:bg-gray-700/40', className)} {...props} />
}

function Td({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('px-4 py-3 text-gray-700 dark:text-gray-300', className)} {...props} />
}

// ---------------------------------------------------------------------------
// Skeleton row
// ---------------------------------------------------------------------------
function TableSkeletonRow({ cols }: { cols: number }) {
  return (
    <Tr>
      {Array.from({ length: cols }).map((_, i) => (
        <Td key={i}>
          <div className="h-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </Td>
      ))}
    </Tr>
  )
}

export { Table, TableHead, Th, TableBody, Tr, Td, TableSkeletonRow }
